import { parseEuroCents } from '../money'
import { romeInstant, romeNow } from '../time'

/**
 * S6 (CMP-3, decision 3): a till's daily closure (the Z) as the counter types
 * it from the printed slip. The server sets it against the till's documents
 * and asks for a reason when they differ.
 */

export interface TillDraft {
  /** The Z's number, as printed. */
  number: string
  /** When it was printed, Rome wall-clock. */
  date: string
  time: string
  /** Its total, net of returns: may be negative on a day of more returns than receipts. */
  total: string
  /** Why it differs from the documents, once the server has said it does. */
  reason: string
}

/** What the API takes: `TillClosurePrinted`. */
export interface TillBody {
  zNumber: number
  closedAt: string
  totalCents: number
  reason?: string
}

/** What the server answered when the Z differs from its till's documents. */
export interface TillDifference {
  documentsCents: number
  differenceCents: number
}

export function newTillDraft(now: Date = new Date()): TillDraft {
  const { date, time } = romeNow(now)
  return { number: '', date, time, total: '', reason: '' }
}

/** "−12,50" or "-12,50" for a day that gave back more than it took. */
export function parseSignedEuroCents(text: string): number | null {
  const negative = /^\s*[-−]/.test(text)
  const cents = parseEuroCents(text.replace(/^\s*[-−]\s*/, ''))
  if (cents === null) return null
  return negative ? -cents : cents
}

const Z_NUMBER = /^\d{1,9}$/

export type TillOutcome =
  | { ok: true; till: TillBody | null }
  | { ok: false; missing: 'incomplete' | 'reason' }

/**
 * What a form sends: nothing when no Z number is typed and the Z is optional
 * (a drawer closed mid-day), the Z otherwise, or what is still missing. A
 * reason goes with it once the server has said the Z differs.
 */
export function tillBody(
  draft: TillDraft,
  options: { optional: boolean; difference: TillDifference | null },
): TillOutcome {
  const number = draft.number.trim()
  if (!number && options.optional && !draft.total.trim()) return { ok: true, till: null }
  const zNumber = Z_NUMBER.test(number) ? Number(number) : 0
  const closedAt = romeInstant(draft.date, draft.time)
  const totalCents = parseSignedEuroCents(draft.total)
  if (zNumber < 1 || !closedAt || totalCents === null) return { ok: false, missing: 'incomplete' }
  const reason = draft.reason.trim()
  if (options.difference && reason.length < 3) return { ok: false, missing: 'reason' }
  return {
    ok: true,
    till: { zNumber, closedAt, totalCents, ...(reason ? { reason } : {}) },
  }
}

/** The server's answer to a Z that differs, from a problem's details. */
export function tillDifferenceOf(details: Record<string, unknown>): TillDifference | null {
  const { documentsCents, differenceCents } = details
  return typeof documentsCents === 'number' && typeof differenceCents === 'number'
    ? { documentsCents, differenceCents }
    : null
}

/**
 * The printed time a form suggests starts from when the Z's number is typed,
 * not from when the screen opened: a Cassa left open while another device
 * rang up receipts would date the Z before them, and show a difference that
 * is not there. A date or time the counter typed is never replaced.
 */
export function followNow(
  before: TillDraft,
  after: TillDraft,
  now: { date: string; time: string },
): TillDraft {
  const startsNumber = before.number.trim() === '' && after.number.trim() !== ''
  const sameTime = before.date === after.date && before.time === after.time
  return startsNumber && sameTime ? { ...after, date: now.date, time: now.time } : after
}

/** A change to the Z's figures makes the server's last answer stale; a reason alone does not. */
export function figuresChanged(before: TillDraft, after: TillDraft): boolean {
  return (
    before.number !== after.number ||
    before.date !== after.date ||
    before.time !== after.time ||
    before.total !== after.total
  )
}
